import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import "./PublicProfile.css";
import { UserContext } from "../context/UserContext";
import StarIcon from "../components/StarIcon";
import { useNavigate } from "react-router-dom";

import Avatar1 from "../components/avatars/avatar1";
import Avatar2 from "../components/avatars/avatar2";
import Avatar3 from "../components/avatars/avatar3";
import Avatar4 from "../components/avatars/avatar4";
import Avatar5 from "../components/avatars/avatar5";
import Avatar6 from "../components/avatars/avatar6";
import Avatar7 from "../components/avatars/avatar7";
import Avatar8 from "../components/avatars/avatar8";
import Avatar9 from "../components/avatars/avatar9";
import AvatarDefault from "../components/avatars/AvatarDefault";

const avatarMap = {
  default: AvatarDefault,
  avatar1: Avatar1,
  avatar2: Avatar2,
  avatar3: Avatar3,
  avatar4: Avatar4,
  avatar5: Avatar5,
  avatar6: Avatar6,
  avatar7: Avatar7,
  avatar8: Avatar8,
  avatar9: Avatar9,
};

const PublicProfile = () => {
  const { userId } = useParams();
  const { user } = useContext(UserContext);

  const [profile, setProfile] = useState({});
  const [completed, setCompleted] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const navigate = useNavigate();

  const currentUserId = user?.id || user?._id;

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [profileRes, completedRes, wishlistRes, reviewsRes] =
          await Promise.all([
            axios.get(`https://bookmatchup.onrender.com/api/user/${userId}`),
            axios.get(
              `https://bookmatchup.onrender.com/api/lists/completed/${userId}`
            ),
            axios.get(
              `https://bookmatchup.onrender.com/api/lists/wishlist/${userId}`
            ),
            axios.get(
              `https://bookmatchup.onrender.com/api/review/user/${userId}`
            ),
          ]);

        setProfile(profileRes.data);
        setCompleted(completedRes.data);
        setWishlist(wishlistRes.data);
        setReviews(reviewsRes.data);

        if (profileRes.data.likes) {
          setLikeCount(profileRes.data.likes.length);
          const isLiked = profileRes.data.likes.includes(currentUserId);
          setLiked(isLiked);
        }
      } catch (err) {
        console.error("Error fetching public profile data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [userId, currentUserId]);

  const handleAddToWishlist = async (book) => {
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      const addBookRes = await axios.post(
        "https://bookmatchup.onrender.com/api/book/add-book",
        {
          itemId: book.itemId,
          title: book.title,
          author_name: book.author_name?.join(", "),
          cover_url: book.cover_url,
          first_publish_year: book.first_publish_year,
          first_sentence: book.first_sentence || "",
          isbn: book.isbn || [],
          author_key: book.author_key || [],
          subject: book.subject || [],
          subject_facet: book.subject_facet || [],
        }
      );
      const bookId = addBookRes.data._id;

      await axios.post("https://bookmatchup.onrender.com/api/lists/wishlist", {
        userId: user.id,
        bookId,
        title: book.title,
        author_name: book.author_name.join(", "),
        cover_url: book.cover_url,
      });
      alert("Book added to wishlist!");
    } catch (err) {
      console.error("❌ Failed to add to wishlist:", err);
      alert("This book is already on your Wishlist.");
    }
  };

  const handleAddToCompleted = async (book) => {
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      const addBookRes = await axios.post(
        "https://bookmatchup.onrender.com/api/book/add-book",
        {
          itemId: book.itemId,
          title: book.title,
          author_name: book.author_name?.join(", "),
          cover_url: book.cover_url,
          first_publish_year: book.first_publish_year,
          first_sentence: book.first_sentence || "",
          isbn: book.isbn || [],
          author_key: book.author_key || [],
          subject: book.subject || [],
          subject_facet: book.subject_facet || [],
        }
      );
      const bookId = addBookRes.data._id;

      await axios.post("https://bookmatchup.onrender.com/api/lists/completed", {
        userId: user.id,
        bookId,
        title: book.title,
        author_name: book.author_name.join(", "),
        cover_url: book.cover_url,
      });
      alert("Book marked as completed!");
    } catch (err) {
      console.error("❌ Failed to add to completed:", err);
      alert("This book is already on Completed list.");
    }
  };

  const handleToggleLike = async () => {
    try {
      await axios.put(
        `https://bookmatchup.onrender.com/api/user/${userId}/like`,
        {
          likerId: currentUserId,
        }
      );

      // Optimistic update: toggle like and count locally
      setLiked((prev) => !prev);
      setLikeCount((prev) => prev + (liked ? -1 : 1));
    } catch (err) {
      console.error("Error toggling like:", err);
    }
  };

  const averageRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(
        1
      )
    : null;

  const renderBookCards = (books) => (
    <div className="book-card-grid">
      {books.map((entry, idx) => {
        const book = entry.bookId || {};
        return (
          <div key={idx} className="book-card">
            {book.cover_url && (
              <img
                src={book.cover_url}
                alt={book.title}
                className="book-cover"
              />
            )}
            <h4 className="book-title-link">
              <Link to={`/book/${encodeURIComponent(book.itemId)}`}>
                {book.title}
              </Link>
            </h4>
            <p className="book-author">
              {book.author_name || "Unknown Author"}
            </p>
            <div className="button-group">
              <button
                className="button-danger"
                onClick={() => handleAddToWishlist(book)}
              >
                Add to Wishlist
              </button>
              <button
                className="button-style"
                onClick={() => handleAddToCompleted(book)}
              >
                Mark as Completed
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  if (loading) return <p>Loading profile...</p>;

  return (
    <div className="profile-container">
      {profile.avatar && (
        <div
          className="profile-avatar"
          style={{ borderColor: profile.avatarColor || "#ccc" }}
        >
          {React.createElement(avatarMap[profile.avatar] || AvatarDefault, {
            color: profile.avatarColor || "#aaaaaa",
            size: 50,
          })}
        </div>
      )}

      <h2 className="profile-username">{profile.username}</h2>

      {user && currentUserId !== userId && (
        <div className="like-button-container">
          <img
            src={
              liked ? "/images/heart-filled.svg" : "/images/heart-outline.svg"
            }
            alt={liked ? "Liked" : "Not liked"}
            className="heart-icon"
          />
          <button onClick={handleToggleLike} className="like-button">
            {liked ? "💔 Unlike" : "❤️ Like"}
          </button>
          {likeCount > 0 && (
            <p className="like-count">
              {likeCount} {likeCount === 1 ? "like" : "likes"}
            </p>
          )}
        </div>
      )}

      {profile.bio && <p className="profile-bio">{profile.bio}</p>}

      <div className="profile-favorites">
        <p className="profile-favorite">
          <strong>📖 Favorite Book:</strong> {profile.favoriteBook || "N/A"}
        </p>
        <p className="profile-favorite">
          <strong>✍️ Favorite Author:</strong> {profile.favoriteAuthor || "N/A"}
        </p>
        <p className="profile-favorite">
          <strong>🎨 Favorite Genre:</strong> {profile.favoriteGenre || "N/A"}
        </p>
      </div>
      <hr className="public-section-divider" />
      <h2 className="profile-section-title">Completed Books</h2>
      <p className="completed-note2">Click a title to view book details.</p>
      {completed.length ? (
        renderBookCards(completed)
      ) : (
        <p>No books marked as completed.</p>
      )}
      <hr className="public-section-divider" />
      <h2 className="profile-section-title">Wishlist</h2>
      <p className="completed-note2">Click a title to view book details.</p>
      {wishlist.length ? (
        renderBookCards(wishlist)
      ) : (
        <p>No books in wishlist.</p>
      )}

      <hr className="public-section-divider" />
      <h2 className="profile-section-title">Reviews</h2>
      {averageRating && (
        <p className="profile-average">
          <strong>{profile.username}'s Average Rating Score:</strong>{" "}
          <span className="score-with-star">
            <span className="highlight-score">{averageRating}</span>
            <StarIcon filled={true} size={22} />
          </span>
        </p>
      )}
      {reviews.length ? (
        <ul className="review-list">
          {reviews.map((r, idx) => (
            <li key={idx} className="review-item">
              <strong>{r.title}</strong> by {r.author_name || "Unknown"}
              <div className="review-stars">
                {r.reviewTitle && (
                  <div className="review-title">{r.reviewTitle}</div>
                )}
                <div className="review-star-icons">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} filled={i < r.rating} size={22} />
                  ))}
                </div>
              </div>
              <div>{r.reviewText}</div>
            </li>
          ))}
        </ul>
      ) : (
        <p>No reviews written yet.</p>
      )}
    </div>
  );
};

export default PublicProfile;
